// Sounds for Lock-In. Lofi tracks are recorded CC0 files (see public/assets/lockin/CREDITS.md); rain and
// soft tones are synthesized with WebAudio. A browser only lets audio start from a tap, so
// createAmbient().play() must be called from the tap that starts the session or changes the sound.
export const SOUNDS = [
  { id: "lofi-stars", label: "Lofi: Lost stars", file: "/assets/lockin/lofi-lost-stars.mp3" },
  { id: "lofi-piano", label: "Lofi: Piano", file: "/assets/lockin/lofi-piano.mp3" },
  { id: "rain", label: "Rain" },
  { id: "tones", label: "Soft tones" },
  { id: "off", label: "Off" },
];

export const DEFAULT_SOUND = "lofi-stars";
export const fileFor = (id) => (SOUNDS.find((s) => s.id === id) || {}).file || null;

const FADE = 1.2; // seconds; no sound should start or stop with a click
const FILE_VOLUME = 0.7;
const TONE_NOTES = [196.0, 246.94, 293.66, 392.0]; // G3 B3 D4 G4: an open, gentle G major

function noiseBuffer(ctx) {
  const len = ctx.sampleRate * 4;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    // brown-ish noise: softer and rounder than white noise
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }
  return buf;
}

function buildRain(ctx, out) {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  src.loop = true;
  const high = ctx.createBiquadFilter();
  high.type = "highpass";
  high.frequency.value = 400;
  const low = ctx.createBiquadFilter();
  low.type = "lowpass";
  low.frequency.value = 5200;
  src.connect(high).connect(low).connect(out);
  src.start();
  return () => src.stop();
}

function buildTones(ctx, out) {
  const stops = [];
  TONE_NOTES.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.value = 0.18;
    // each note swells and fades on its own slow cycle so the chord keeps drifting
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05 + i * 0.017;
    const depth = ctx.createGain();
    depth.gain.value = 0.16;
    lfo.connect(depth).connect(gain.gain);
    osc.connect(gain).connect(out);
    osc.start();
    lfo.start();
    stops.push(() => osc.stop(), () => lfo.stop());
  });
  return () => stops.forEach((s) => s());
}

// Ramp an <audio> element's volume. (iOS ignores volume and plays at full level; the files are
// loudness-matched so that is still comfortable.)
function rampVolume(audio, to, seconds, done) {
  const from = audio.volume;
  const start = Date.now();
  const id = setInterval(() => {
    const t = Math.min(1, (Date.now() - start) / (seconds * 1000));
    try {
      audio.volume = Math.min(1, Math.max(0, from + (to - from) * t));
    } catch (e) {
      /* read-only on some browsers */
    }
    if (t >= 1) {
      clearInterval(id);
      if (done) done();
    }
  }, 50);
  return () => clearInterval(id);
}

export function createAmbient() {
  let ctx = null;
  let master = null;
  let voice = null; // { fadeOut(), kill() }

  const ensureContext = () => {
    const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
    if (!AC) return false;
    if (!ctx) {
      ctx = new AC();
      master = ctx.createGain();
      master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") ctx.resume();
    return true;
  };

  const kill = () => {
    if (voice) voice.kill();
    voice = null;
  };

  const fileVoice = (src) => {
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = "auto";
    try {
      audio.volume = 0;
    } catch (e) {
      /* ignore */
    }
    const playing = audio.play();
    if (playing && typeof playing.catch === "function") playing.catch(() => {});
    let cancelRamp = rampVolume(audio, FILE_VOLUME, FADE);
    return {
      fadeOut() {
        cancelRamp();
        cancelRamp = rampVolume(audio, 0, FADE, () => this.kill());
      },
      kill() {
        cancelRamp();
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      },
    };
  };

  const synthVoice = (id) => {
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(id === "rain" ? 0.5 : 0.35, ctx.currentTime + FADE);
    const stopSource = id === "rain" ? buildRain(ctx, master) : buildTones(ctx, master);
    let timer = null;
    return {
      fadeOut() {
        master.gain.cancelScheduledValues(ctx.currentTime);
        master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
        master.gain.linearRampToValueAtTime(0, ctx.currentTime + FADE);
        timer = setTimeout(() => this.kill(), FADE * 1000 + 50);
      },
      kill() {
        clearTimeout(timer);
        try {
          stopSource();
        } catch (e) {
          /* already stopped */
        }
      },
    };
  };

  return {
    // `id` is one of SOUNDS. Returns false when nothing plays ("off", unknown, or no audio support).
    play(id) {
      kill();
      const file = fileFor(id);
      if (file) {
        if (typeof Audio === "undefined") return false;
        voice = fileVoice(file);
        return true;
      }
      if (id !== "rain" && id !== "tones") return false;
      if (!ensureContext()) return false;
      voice = synthVoice(id);
      return true;
    },
    // Fade out, then stop. Safe to call twice.
    stop() {
      if (!voice) return;
      const v = voice;
      voice = null;
      v.fadeOut();
    },
    // One soft two-note chime for the end of a session. Works even when the sound is "off".
    chime() {
      if (!ensureContext()) return;
      const t = ctx.currentTime;
      [523.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0, t + i * 0.28);
        g.gain.linearRampToValueAtTime(0.22, t + i * 0.28 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.28 + 1.6);
        osc.connect(g).connect(ctx.destination);
        osc.start(t + i * 0.28);
        osc.stop(t + i * 0.28 + 1.7);
      });
    },
    // Immediate teardown for unmount.
    close() {
      kill();
      if (ctx) {
        try {
          ctx.close();
        } catch (e) {
          /* ignore */
        }
      }
      ctx = null;
      master = null;
    },
  };
}
